import React, { useMemo, useState } from 'react';
import { EcxDateRangePicker } from '../../../common/EcxDatePicker';
import {
    DEFAULT_RECALL_FILTERS,
    RECALL_SEARCH_FIELDS,
    RECALL_STATUSES,
    countActiveRecallFilters,
    getActiveRecallFilterChips,
} from './recallsQuery';

function RecallFilterField({ label, icon, children }) {
    return (
        <div className="recall-filter-field">
            <label className="recall-filter-field__label">
                {icon && <i className={`bi ${icon}`}></i>}
                {label}
            </label>
            {children}
        </div>
    );
}

function RecallFilters({
    filters,
    appliedFilters,
    onFiltersChange,
    onApply,
    onClear,
    onRemoveChip,
    loading = false,
}) {
    const [expanded, setExpanded] = useState(false);
    const activeChips = useMemo(() => getActiveRecallFilterChips(appliedFilters), [appliedFilters]);
    const pendingChanges = useMemo(
        () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
        [filters, appliedFilters]
    );
    const activeFilterCount = countActiveRecallFilters(appliedFilters);

    const updateFilter = (field, value) => {
        onFiltersChange((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div className="recall-filter-panel">
            <div className="recall-filter-panel__header">
                <button
                    type="button"
                    className="recall-filter-panel__toggle"
                    onClick={() => setExpanded((open) => !open)}
                    aria-expanded={expanded}
                >
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-funnel"></i>
                        <span className="fw-normal">Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="recall-filter-panel__count">{activeFilterCount}</span>
                        )}
                    </span>
                    <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
                </button>

                {activeChips.length > 0 && (
                    <div className="recall-filter-panel__chips">
                        {activeChips.map((chip) => (
                            <button
                                key={chip.key}
                                type="button"
                                className="recall-filter-chip"
                                onClick={() => onRemoveChip(chip.key, chip.clearValue)}
                                disabled={loading}
                            >
                                {chip.label}
                                <i className="bi bi-x-lg"></i>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {expanded && (
                <div className="recall-filter-panel__body">
                    <div className="recall-filter-grid">
                        <RecallFilterField label="Search in" icon="bi-search">
                            <select
                                className="form-select form-select-sm"
                                value={filters.searchField}
                                onChange={(event) => updateFilter('searchField', event.target.value)}
                                disabled={loading}
                            >
                                {RECALL_SEARCH_FIELDS.map((field) => (
                                    <option key={field.value} value={field.value}>
                                        {field.label}
                                    </option>
                                ))}
                            </select>
                        </RecallFilterField>

                        <RecallFilterField label="Search term" icon="bi-type">
                            <input
                                type="search"
                                className="form-control form-control-sm"
                                placeholder="Search recall requests..."
                                value={filters.searchTerm}
                                onChange={(event) => updateFilter('searchTerm', event.target.value)}
                                disabled={loading}
                            />
                        </RecallFilterField>

                        <RecallFilterField label="Status" icon="bi-activity">
                            <select
                                className="form-select form-select-sm"
                                value={filters.status}
                                onChange={(event) => updateFilter('status', event.target.value)}
                                disabled={loading}
                            >
                                {RECALL_STATUSES.map((status) => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </RecallFilterField>

                        <RecallFilterField label="Created date range" icon="bi-calendar3">
                            <EcxDateRangePicker
                                startValue={filters.dateFrom}
                                endValue={filters.dateTo}
                                onStartChange={(value) => updateFilter('dateFrom', value)}
                                onEndChange={(value) => updateFilter('dateTo', value)}
                                disabled={loading}
                                size="sm"
                            />
                        </RecallFilterField>
                    </div>

                    <div className="recall-filter-panel__actions">
                        <button
                            type="button"
                            className="btn btn-light btn-sm rounded-pill"
                            onClick={() => onFiltersChange(DEFAULT_RECALL_FILTERS)}
                            disabled={loading}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            className="btn btn-light-primary btn-sm rounded-pill"
                            onClick={() => onApply(filters)}
                            disabled={loading || !pendingChanges}
                        >
                            Apply filters
                        </button>
                        {activeFilterCount > 0 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-muted"
                                onClick={onClear}
                                disabled={loading}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default RecallFilters;
